import { Injectable } from '@angular/core';
import { DateTime } from 'luxon';

import calendarData from 'src/shared/constants/demo/demo-data.json';
import messagesData from 'src/shared/constants/demo/messages.json';
import contactsData from 'src/shared/constants/demo/contacts.json';
import billsData from 'src/shared/constants/demo/bills.json';
import checklistsData from 'src/shared/constants/demo/checklists.json';
import upsellsData from 'src/shared/constants/demo/upsells.json';
import inventoryData from 'src/shared/constants/demo/inventory.json';
import marketingData from 'src/shared/constants/demo/marketing.json';
import financeData from 'src/shared/constants/demo/finance.json';
import maintenanceData from 'src/shared/constants/demo/maintenance.json';
import inquiriesData from 'src/shared/constants/demo/inquiries.json';
import previewOnlyData from 'src/shared/constants/demo/preview-only.json';

import { environment } from 'src/environments/environment';

// Each domain lives in its own JSON file (keyed by endpoint path) and is merged
// into one lookup. Keys must not collide across files.
const demoData: Record<string, any> = {
  ...calendarData,
  ...messagesData,
  ...contactsData,
  ...billsData,
  ...checklistsData,
  ...upsellsData,
  ...inventoryData,
  ...marketingData,
  ...financeData,
  ...maintenanceData,
  ...inquiriesData,
};

export const DEMO_ACCOUNT_EMAIL = 'demos@tidy.com';
export const DEMO_ACCOUNT_FLAG_KEY = 'isDemoAccount';
export const DEMO_ADDRESSES_KEY = 'demoAddresses';
// Offline preview mode (product-owner click-through, no backend/login). Kept
// separate from DEMO_ACCOUNT_FLAG_KEY so demo-account-only UI (DemoBlockService's
// "create an account" modal, the demo entrance/welcome sweep) stays off in
// preview — preview only needs GETs template-served, not the sales-demo experience.
export const PREVIEW_MODE_KEY = 'isPreviewMode';

// MED is zero-padded ('Jun 05') to match the real API; some consumers
// (e.g. schedule-monthly-view) re-parse these strings with strict 'MMM dd, yyyy'.
const DATE_FORMATS = {
  MED: 'MMM dd, yyyy',
  SLASH: 'MM/dd/yyyy',
  ISO: 'yyyy-MM-dd',
};

interface DemoAddress {
  id: number;
  timezone: string;
  address_name?: string;
  address1?: string;
  city?: string;
  state?: string;
  zip?: string;
}

/**
 * Returns hard-coded demo responses for GET endpoints when the user is logged
 * into the sales demo account.
 *
 * Templates in demo-data.json are keyed by endpoint path. A '*' key segment
 * matches any single path segment (e.g. "api/v1/customer/task-lists/* /tasks").
 * Each entry takes one of three forms:
 * - { "days": [...] }        calendar-view shape; honors calendar query params
 * - { "response": ... }      returned as-is; array responses honor the generic
 *                            list filters (page, filters[address_id(s)], etc.)
 * - { "byPathParam": {...} } for wildcard keys; looked up by the first '*'
 *                            segment's value (e.g. a detail page id). An id with
 *                            no entry yields an empty body, never a fall-through.
 *
 * Placeholder tokens materialized at runtime:
 * - "ADDRESS_ID_<n>" / "ADDRESS_TZ_<n>": the nth address captured at login
 * - "DATE(<offset>,MED|SLASH|ISO)": today + offset days
 * - "DATETIME(<offset>,<HH:mm>)": ISO timestamp at today + offset days
 */
@Injectable({ providedIn: 'root' })
export class DemoDataService {

  isDemoAccount(): boolean {
    return localStorage.getItem(DEMO_ACCOUNT_FLAG_KEY) === 'true';
  }

  isPreviewMode(): boolean {
    return localStorage.getItem(PREVIEW_MODE_KEY) === 'true';
  }

  // What DemoInterceptor calls to decide whether to template-serve a GET.
  // Deliberately an OR of the two flags rather than folding preview into
  // isDemoAccount(): DemoAccountService.isDemoSession() must keep reading only
  // the demo-account flag so demo-only UI never fires for a preview session.
  shouldServeTemplates(): boolean {
    return this.isDemoAccount() || this.isPreviewMode();
  }

  getDemoData(url: string): any | null {
    const addresses = this.getDemoAddresses();
    if (addresses.length === 0) {
      return null;
    }
    const [path, query] = url.split('?');
    const match = this.findTemplate(this.normalizePath(path));
    if (!match) {
      return null;
    }
    const params = new URLSearchParams(query || '');
    const { template, pathParams } = match;
    if (template.days) {
      const days = this.materialize(template.days, addresses);
      return this.applyCalendarParams(this.dropMissingAddressEvents(days), params);
    }
    if (template.byPathParam) {
      const entry = template.byPathParam[pathParams[0]];
      if (entry === undefined) {
        // The endpoint IS templated, so the id can only have come from demo data.
        // Returning null would let the interceptor fall through and hit the real
        // backend with a mock id, which 403s (the ids collide with unrelated
        // production records). Serve an empty body shaped like the entries that
        // do exist instead.
        return Object.values(template.byPathParam).some(Array.isArray) ? [] : {};
      }
      return this.materialize(entry, addresses);
    }
    if (template.response !== undefined) {
      const response = this.materialize(template.response, addresses);
      return Array.isArray(response) ? this.applyListParams(response, params) : response;
    }
    return null;
  }

  private normalizePath(path: string): string {
    if (path.startsWith(environment.api_url)) {
      path = path.slice(environment.api_url.length);
    }
    return path.startsWith('/') ? path.slice(1) : path;
  }

  private findTemplate(path: string): { template: any, pathParams: string[] } | null {
    // Preview-only templates take precedence in preview mode. They cover
    // endpoints the sales demo account must keep passing through to the real
    // backend (e.g. /me carries its real login identity, and /addresses is what
    // seeds DEMO_ADDRESSES_KEY — the ids every other template's ADDRESS_ID_n
    // token resolves against), so they must never enter the shared registry.
    if (this.isPreviewMode()) {
      const previewMatch = this.findTemplateIn(previewOnlyData, path);
      if (previewMatch) {
        return previewMatch;
      }
    }
    return this.findTemplateIn(demoData, path);
  }

  private findTemplateIn(templates: Record<string, any>, path: string): { template: any, pathParams: string[] } | null {
    if (templates[path]) {
      return { template: templates[path], pathParams: [] };
    }
    for (const key of Object.keys(templates)) {
      if (!key.includes('*')) {
        continue;
      }
      const keySegments = key.split('/');
      const pathSegments = path.split('/');
      if (keySegments.length !== pathSegments.length) {
        continue;
      }
      const pathParams = [];
      const matches = keySegments.every((segment, index) => {
        if (segment === '*') {
          pathParams.push(pathSegments[index]);
          return true;
        }
        return segment === pathSegments[index];
      });
      if (matches) {
        return { template: templates[key], pathParams };
      }
    }
    return null;
  }

  private getDemoAddresses(): DemoAddress[] {
    try {
      const addresses = JSON.parse(localStorage.getItem(DEMO_ADDRESSES_KEY) || '[]');
      return Array.isArray(addresses) ? addresses : [];
    } catch (_e) {
      return [];
    }
  }

  private materialize(template: any, addresses: DemoAddress[]): any {
    const today = DateTime.local().startOf('day');
    const addressAt = (n: number) => addresses[n - 1];
    let raw = JSON.stringify(template);
    // Tokens beyond the available addresses get a negative sentinel id; their
    // entries are dropped so a short address list never double-books a property.
    raw = raw.replace(/"ADDRESS_ID_(\d+)"/g, (_match, n) => String(addressAt(+n)?.id ?? -n));
    // Inline forms for tokens embedded inside a longer string value
    // (e.g. "property: {{ADDR_ID_1}}\n..." or "the {{ADDR_NAME_1}} turnover").
    raw = raw.replace(/\{\{ADDR_ID_(\d+)\}\}/g, (_match, n) => String(addressAt(+n)?.id ?? -n));
    raw = raw.replace(/\{\{ADDR_NAME_(\d+)\}\}/g, (_match, n) => addressAt(+n)?.address_name || addressAt(+n)?.address1 || `Property ${n}`);
    raw = raw.replace(/\{\{DATE\((-?\d+),(MED|SLASH|ISO)\)\}\}/g, (_match, offset, format) => today.plus({ days: +offset }).toFormat(DATE_FORMATS[format]));
    raw = raw.replace(/"ADDRESS_TZ_(\d+)"/g, (_match, n) => JSON.stringify(addressAt(+n)?.timezone || 'America/Los_Angeles'));
    raw = raw.replace(/"ADDRESS_NAME_(\d+)"/g, (_match, n) => JSON.stringify(addressAt(+n)?.address_name || addressAt(+n)?.address1 || `Property ${n}`));
    raw = raw.replace(/"ADDRESS_LINE1_(\d+)"/g, (_match, n) => JSON.stringify(addressAt(+n)?.address1 || ''));
    raw = raw.replace(/"ADDRESS_CITY_(\d+)"/g, (_match, n) => JSON.stringify(addressAt(+n)?.city || ''));
    raw = raw.replace(/"ADDRESS_STATE_(\d+)"/g, (_match, n) => JSON.stringify(addressAt(+n)?.state || ''));
    raw = raw.replace(/"ADDRESS_ZIP_(\d+)"/g, (_match, n) => JSON.stringify(addressAt(+n)?.zip || ''));
    raw = raw.replace(/"DATE\((-?\d+),(MED|SLASH|ISO)\)"/g, (_match, offset, format) => JSON.stringify(today.plus({ days: +offset }).toFormat(DATE_FORMATS[format])));
    raw = raw.replace(/"DATETIME\((-?\d+),(\d{2}):(\d{2})\)"/g, (_match, offset, hour, minute) => JSON.stringify(today.plus({ days: +offset }).set({ hour: +hour, minute: +minute }).toUTC().toISO()));
    const materialized = JSON.parse(raw);
    return Array.isArray(materialized)
      ? materialized.filter((entry) => entry?.address_id === undefined || entry.address_id > 0)
      : materialized;
  }

  private dropMissingAddressEvents(days: any[]): any[] {
    return days.map((day) => ({
      ...day,
      jobs: day.jobs.filter((job) => job.address_id > 0),
      reservations: day.reservations.filter((reservation) => reservation.address_id > 0),
      inquiries: (day.inquiries || []).filter((inquiry) => inquiry.address_id > 0),
      availability_requests: day.availability_requests.filter((request) => request.address_id > 0),
    }));
  }

  // Generic filters for flat list responses. Each filter applies only when the
  // param is present; entries missing the filtered field pass through.
  private applyListParams(list: any[], params: URLSearchParams): any[] {
    const page = params.get('page');
    if (page && +page > 1) {
      return [];
    }
    const matchOrMissing = (value: any, predicate: (value: any) => boolean) => value === undefined || predicate(value);
    // Both the bracketed (filters[address_ids]) and bare (address_ids, used by bills) forms.
    const addressIdsParam = params.get('filters[address_ids]') || params.get('address_ids');
    if (addressIdsParam) {
      const addressIds = addressIdsParam.split(',').map(Number);
      list = list.filter((entry) => {
        const entryIds = entry.address_ids ?? entry.address_id;
        return matchOrMissing(entryIds, (ids) => Array.isArray(ids) ? ids.some((id) => addressIds.includes(id)) : addressIds.includes(ids));
      });
    }
    const addressTaxIdsParam = params.get('address_tax_ids');
    if (addressTaxIdsParam) {
      const addressTaxIds = addressTaxIdsParam.split(',').map(Number);
      list = list.filter((entry) => matchOrMissing(entry.address_tax_id, (id) => addressTaxIds.includes(id)));
    }
    // Bills status chips: pending -> unpaid, paid -> paid, all -> param omitted.
    const statusParam = params.get('status');
    if (statusParam) {
      const statuses = statusParam.split(',');
      const wantsPaid = statuses.includes('paid');
      const wantsUnpaid = statuses.includes('pending') || statuses.includes('unpaid');
      if (wantsPaid !== wantsUnpaid) {
        list = list.filter((entry) => matchOrMissing(entry.full_paid, (paid) => paid === wantsPaid));
      }
    }
    const addressIdParam = params.get('filters[address_id]') || params.get('address_id');
    if (addressIdParam) {
      list = list.filter((entry) => matchOrMissing(entry.address_id, (id) => id === +addressIdParam));
    }
    const consumableParam = params.get('filters[consumable]');
    if (consumableParam) {
      list = list.filter((entry) => matchOrMissing(entry.consumable, (consumable) => consumable === (consumableParam === 'true')));
    }
    const nameSearchParam = params.get('filters[name_search]');
    if (nameSearchParam) {
      list = list.filter((entry) => matchOrMissing(entry.name, (name) => String(name).toLowerCase().includes(nameSearchParam.toLowerCase())));
    }
    const chatRoomKeyParam = params.get('filters[chat_room_key]');
    if (chatRoomKeyParam) {
      list = list.filter((entry) => matchOrMissing(entry.chat_room_key, (key) => key === chatRoomKeyParam));
    }
    const teamIdParam = params.get('filters[team_id]');
    if (teamIdParam) {
      list = list.filter((entry) => matchOrMissing(entry.team_id, (teamId) => +teamId === +teamIdParam));
    }
    // Concierge-items is one endpoint serving two pages: upsells requests
    // guest-upsell types (concierge_item_type_key=...) while chat excludes them
    // (not_concierge_item_type_key=...), so both filters must be honored or the
    // upsell entries leak into the chat session list.
    const typeKeyParam = params.get('concierge_item_type_key');
    if (typeKeyParam) {
      const typeKeys = typeKeyParam.split(',');
      list = list.filter((entry) => matchOrMissing(entry.concierge_item_type_key, (key) => typeKeys.includes(key)));
    }
    const notTypeKeyParam = params.get('not_concierge_item_type_key');
    if (notTypeKeyParam) {
      const excludedTypeKeys = notTypeKeyParam.split(',');
      list = list.filter((entry) => matchOrMissing(entry.concierge_item_type_key, (key) => !excludedTypeKeys.includes(key)));
    }
    return list;
  }

  private applyCalendarParams(days: any[], params: URLSearchParams): any[] {
    const startDate = DateTime.fromISO(params.get('calendar_start_date') || '');
    const endDate = DateTime.fromISO(params.get('calendar_end_date') || '');
    if (startDate.isValid && endDate.isValid) {
      days = days.filter((day) => {
        const dayDate = DateTime.fromFormat(day.date, DATE_FORMATS.MED);
        return dayDate >= startDate.startOf('day') && dayDate <= endDate.startOf('day');
      });
    }
    const scope = params.get('scope');
    if (scope) {
      days = days.map((day) => ({
        ...day,
        jobs: scope === 'jobs' ? day.jobs : [],
        reservations: scope === 'reservations' ? day.reservations : [],
        inquiries: scope === 'inquiries' ? (day.inquiries || []) : [],
        availability_requests: scope === 'availability_requests' ? day.availability_requests : [],
      }));
    }
    const addressIdsParam = params.get('filters[address_ids]');
    if (addressIdsParam) {
      const addressIds = addressIdsParam.split(',').map(Number);
      days = days.map((day) => ({
        ...day,
        jobs: day.jobs.filter((job) => addressIds.includes(job.address_id)),
        reservations: day.reservations.filter((reservation) => addressIds.includes(reservation.address_id)),
        inquiries: (day.inquiries || []).filter((inquiry) => addressIds.includes(inquiry.address_id)),
        availability_requests: day.availability_requests.filter((request) => addressIds.includes(request.address_id)),
      }));
    }
    const homekeeperIdsParam = params.get('filters[homekeeper_ids]');
    if (homekeeperIdsParam) {
      const homekeeperIds = homekeeperIdsParam.split(',').map(Number);
      days = days.map((day) => ({
        ...day,
        jobs: day.jobs.filter((job) => job.homekeeper_jobs?.some((homekeeperJob) => homekeeperIds.includes(homekeeperJob.homekeeper?.id))),
      }));
    }
    return days;
  }
}
